import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-toastify';
import {
  PageHeader,
  DataTable,
  StatusBadge,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { usersApi } from '../../api/users';
import { formatCurrency, formatDate, initials } from '../../utils';
import type {
  Customer,
  Payment,
  Review,
  ServiceRequest,
} from '../../types';

interface TabPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) => {
  if (value !== index) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500} sx={{ mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [tab, setTab] = useState(0);
  const [activityTab, setActivityTab] = useState(0);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [jobs, setJobs] = useState<ServiceRequest[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  const loadCustomer = useCallback(async () => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await usersApi.getUserById(id);
      setCustomer(data);
      setForm({ name: data.name, email: data.email, phone: data.phone });
      setNotFound(false);
    } catch {
      setNotFound(true);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  useEffect(() => {
    if (!customer) return;
    setRequests([]);
    setJobs([]);
    setPayments([]);
    usersApi
      .getUserReviews(customer.id, customer.name)
      .then(setReviews)
      .catch(() => setReviews([]));
  }, [customer]);

  const handleSaveEdit = async () => {
    if (!customer) return;
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Name, email, and phone are required');
      return;
    }
    setEditLoading(true);
    try {
      // User update endpoint is not available on admin user APIs yet.
      toast.info('User update API is not available yet');
      setEditOpen(false);
    } catch {
      toast.error('Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const requestColumns: Column<ServiceRequest>[] = useMemo(
    () => [
      { id: 'id', label: 'ID', render: (r) => r.id },
      { id: 'category', label: 'Category', render: (r) => r.category },
      {
        id: 'createdAt',
        label: 'Created',
        render: (r) => formatDate(r.createdAt),
      },
      {
        id: 'status',
        label: 'Status',
        render: (r) => <StatusBadge status={r.status} />,
      },
    ],
    []
  );

  const jobColumns: Column<ServiceRequest>[] = useMemo(
    () => [
      { id: 'id', label: 'ID', render: (r) => r.id },
      { id: 'category', label: 'Category', render: (r) => r.category },
      {
        id: 'provider',
        label: 'Provider',
        render: (r) => r.providerName || '—',
      },
      {
        id: 'status',
        label: 'Status',
        render: (r) => <StatusBadge status={r.status} />,
      },
    ],
    []
  );

  const reviewColumns: Column<Review>[] = useMemo(
    () => [
      { id: 'id', label: 'ID', render: (r) => r.id },
      { id: 'rating', label: 'Rating', render: (r) => `${r.rating}/5` },
      {
        id: 'party',
        label: 'Reviewed',
        render: (r) => r.reviewedParty,
      },
      {
        id: 'date',
        label: 'Date',
        render: (r) => formatDate(r.date),
      },
      {
        id: 'status',
        label: 'Status',
        render: (r) => <StatusBadge status={r.status} />,
      },
    ],
    []
  );

  const paymentColumns: Column<Payment>[] = useMemo(
    () => [
      { id: 'id', label: 'ID', render: (r) => r.id },
      {
        id: 'amount',
        label: 'Amount',
        render: (r) => formatCurrency(r.amount),
      },
      { id: 'method', label: 'Method', render: (r) => r.method },
      {
        id: 'date',
        label: 'Date',
        render: (r) => formatDate(r.date),
      },
      {
        id: 'status',
        label: 'Status',
        render: (r) => <StatusBadge status={r.status} />,
      },
    ],
    []
  );

  if (loading) {
    return (
      <Box>
        <PageHeader title="User Details" subtitle="Loading…" />
      </Box>
    );
  }

  if (notFound || !customer) {
    return (
      <Box>
        <PageHeader
          title="User not found"
          subtitle="This customer does not exist or was removed"
          breadcrumbs={[
            { label: 'Users', to: '/users' },
            { label: 'Not found' },
          ]}
          actions={
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/users')}
              variant="outlined"
            >
              Back to Users
            </Button>
          }
        />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={customer.name}
        subtitle={customer.email}
        breadcrumbs={[
          { label: 'Users', to: '/users' },
          { label: customer.name },
        ]}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/users')}
            variant="outlined"
          >
            Back
          </Button>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Personal Information" />
        <Tab label="Addresses" />
        <Tab label="Activity" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={customer.avatar}
                  sx={{
                    width: 72,
                    height: 72,
                    fontSize: 24,
                    bgcolor: 'primary.main',
                  }}
                >
                  {initials(customer.name)}
                </Avatar>
                <Box>
                  <Typography variant="h3">{customer.name}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <StatusBadge status={customer.status} />
                  </Box>
                </Box>
              </Box>
              <Button
                startIcon={<EditIcon />}
                variant="outlined"
                onClick={() => {
                  setForm({
                    name: customer.name,
                    email: customer.email,
                    phone: customer.phone,
                  });
                  setEditOpen(true);
                }}
              >
                Edit
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow label="Email" value={customer.email} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow label="Phone" value={customer.phone} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow
                  label="Registration Date"
                  value={formatDate(customer.registrationDate)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow label="User ID" value={customer.id} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow
                  label="Status"
                  value={<StatusBadge status={customer.status} />}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Grid container spacing={2}>
          {customer.addresses.length === 0 ? (
            <Grid item xs={12}>
              <Typography color="text.secondary">No addresses on file.</Typography>
            </Grid>
          ) : (
            customer.addresses.map((addr) => (
              <Grid item xs={12} md={6} key={addr.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography variant="h3">{addr.label}</Typography>
                      {addr.isDefault && (
                        <Chip label="Default" size="small" color="primary" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {addr.fullAddress}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }} fontWeight={500}>
                      {addr.city}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Tabs
          value={activityTab}
          onChange={(_, v) => setActivityTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
        >
          <Tab label={`Service Requests (${requests.length})`} />
          <Tab label={`Jobs (${jobs.length})`} />
          <Tab label={`Reviews (${reviews.length})`} />
          <Tab label={`Payments (${payments.length})`} />
        </Tabs>

        <TabPanel value={activityTab} index={0}>
          <DataTable
            columns={requestColumns}
            rows={requests}
            emptyTitle="No service requests"
            emptyDescription="This user has not submitted any requests."
            getRowId={(r) => r.id}
          />
        </TabPanel>
        <TabPanel value={activityTab} index={1}>
          <DataTable
            columns={jobColumns}
            rows={jobs}
            emptyTitle="No jobs"
            emptyDescription="No jobs linked to this user."
            getRowId={(r) => r.id}
          />
        </TabPanel>
        <TabPanel value={activityTab} index={2}>
          <DataTable
            columns={reviewColumns}
            rows={reviews}
            emptyTitle="No reviews"
            emptyDescription="No reviews involving this user."
            getRowId={(r) => r.id}
          />
        </TabPanel>
        <TabPanel value={activityTab} index={3}>
          <DataTable
            columns={paymentColumns}
            rows={payments}
            emptyTitle="No payments"
            emptyDescription="No payments recorded for this user."
            getRowId={(r) => r.id}
          />
        </TabPanel>
      </TabPanel>

      <Dialog
        open={editOpen}
        onClose={editLoading ? undefined : () => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetailPage;
